import {
  AddTextEditorContent,
  AddTextInfo,
  AppliedProperty,
  PropertyValue,
  EpigraphicUnitSide,
  RowTypes,
} from '@oare/types';
import { isObject } from '@oare/oare';

// What the Add Texts page keeps in localStorage so a failed step, a failed
// Submit or a reload never loses the typed text. Signs are not stored: the
// editor derives them again from the text when it restores a row.
export interface DraftRow {
  type: RowTypes;
  text?: string;
  lineValue?: number;
  reading?: string;
}

export interface DraftColumn {
  rows: DraftRow[];
}

export interface DraftSide {
  side: EpigraphicUnitSide;
  columns: DraftColumn[];
}

export type DraftTextInfo = Omit<AddTextInfo, 'properties'>;

export interface AddTextDraft {
  textInfo?: DraftTextInfo;
  // Additional properties are not kept (the properties tree cannot be
  // preselected from them); this only tells the user to select them again.
  hadProperties?: boolean;
  sides: DraftSide[];
  // The uuid the new text will get, kept so a reload submits the same text.
  newTextUuid?: string;
}

// Identifies one draft: whose it is and which import it belongs to.
export interface DraftOwner {
  userUuid?: string;
  collectionUuid: string;
  textUuid?: string;
}

const draftKey = ({ userUuid, collectionUuid, textUuid }: DraftOwner) =>
  `oare:add-text-draft:${userUuid || 'anonymous'}:${collectionUuid}:${
    textUuid || 'new'
  }`;

export const clearDraft = (owner: DraftOwner) => {
  try {
    localStorage.removeItem(draftKey(owner));
  } catch {
    // Nothing to clear.
  }
};

// Anything under the key that is not a draft this code wrote (an older or
// newer shape, a hand edit) counts as no draft, so it can never break the page.
const isDraft = (value: unknown): value is AddTextDraft =>
  isObject(value) &&
  (value.textInfo === undefined || isObject(value.textInfo)) &&
  (value.newTextUuid === undefined || typeof value.newTextUuid === 'string') &&
  Array.isArray(value.sides) &&
  value.sides.every(
    side =>
      isObject(side) &&
      typeof side.side === 'string' &&
      Array.isArray(side.columns) &&
      side.columns.every(
        column =>
          isObject(column) &&
          Array.isArray(column.rows) &&
          column.rows.every(
            row => isObject(row) && typeof row.type === 'string'
          )
      )
  );

export const loadDraft = (owner: DraftOwner): AddTextDraft | null => {
  try {
    const stored = localStorage.getItem(draftKey(owner));
    if (!stored) {
      return null;
    }
    const draft: unknown = JSON.parse(stored);
    if (isDraft(draft)) {
      return draft;
    }
  } catch {
    // Unreadable JSON: drop it below.
  }
  clearDraft(owner);
  return null;
};

/**
 * Stores the draft. Returns 'saved', 'too-large' when the browser's storage
 * quota is exceeded, or 'unavailable' when storage is blocked; the page
 * works either way, only without a draft.
 */
export const saveDraft = (
  owner: DraftOwner,
  draft: AddTextDraft
): 'saved' | 'too-large' | 'unavailable' => {
  try {
    localStorage.setItem(draftKey(owner), JSON.stringify(draft));
    return 'saved';
  } catch (err) {
    return (err as { name?: string }).name === 'QuotaExceededError'
      ? 'too-large'
      : 'unavailable';
  }
};

export const draftSidesFromContent = (
  content: AddTextEditorContent
): DraftSide[] =>
  content.sides.map(side => ({
    side: side.type,
    columns: side.columns.map(column => ({
      rows: column.rows.map(row => ({
        type: row.type,
        text: row.text,
        lineValue: row.value && row.value > 1 ? row.value : undefined,
        reading: row.reading,
      })),
    })),
  }));

export const draftTextInfo = (textInfo: AddTextInfo): DraftTextInfo => {
  const { properties: _properties, ...rest } = textInfo;
  return rest;
};

// The Text Info properties tree starts at "Document - Text" and applies the
// classification above it by itself; only properties beyond those count as
// the user's own choices.
export const TEXT_PROPERTIES_START_UUID =
  'cdd5e5a9-55f2-11eb-bf9e-024de1c1cc1d';

const leadsToValue = (value: PropertyValue, hierarchyUuid: string): boolean =>
  value.hierarchy.uuid === hierarchyUuid ||
  (value.variables || []).some(variable =>
    (variable.values || []).some(child => leadsToValue(child, hierarchyUuid))
  );

export const hasChosenProperties = (properties: AppliedProperty[]): boolean =>
  properties.some(
    property =>
      !property.valueRow ||
      !leadsToValue(property.valueRow, TEXT_PROPERTIES_START_UUID)
  );

export interface DraftSaver {
  // Saves after `delayMs` without another call.
  schedule: () => void;
  // Saves a pending save at once.
  flush: () => void;
  // Ends saving for this page: drops a pending save, clears the stored
  // draft, and ignores every later schedule or flush (a late editor update
  // or the unload flush cannot bring a discarded or submitted draft back).
  close: () => void;
}

export const createDraftSaver = (
  write: () => void,
  clear: () => void,
  delayMs = 300
): DraftSaver => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  const cancel = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
  return {
    schedule: () => {
      if (closed) {
        return;
      }
      cancel();
      timer = setTimeout(() => {
        timer = undefined;
        write();
      }, delayMs);
    },
    flush: () => {
      if (closed || timer === undefined) {
        return;
      }
      cancel();
      write();
    },
    close: () => {
      closed = true;
      cancel();
      clear();
    },
  };
};

/**
 * Waits for the initial children to report their content. Duplicate reports and
 * children added during restoration cannot complete another child's report.
 */
export const createRestoreCounter = (
  expected: string[],
  onDone: () => void
) => {
  const pending = new Set(expected);
  let done = false;
  const check = () => {
    if (!done && pending.size === 0) {
      done = true;
      onDone();
    }
  };
  return {
    start: check,
    report: (uuid: string) => {
      pending.delete(uuid);
      check();
    },
  };
};
